import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { OmniReportContentType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { CheckBoxFormField } from '~components/FormFields/CheckBoxFormField';
import { RadioGroupFormField } from '~components/FormFields/RadioGroupFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { OMNI_BUILDER_CONTENT_ID } from '~constants/consts';
import { TREAD_UUID } from '~hooks/useCompany/useCompany';
import { OmniReport, ReportCategory } from '~hooks/useOmniReports/models';
import { useOmniReports } from '~hooks/useOmniReports/useOmniReports';
import { InfoIcon } from '~icons/InfoIcon';

interface ReportCategoryDialogProps {
  companyId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: (reportCategory: OmniReport) => void;
  defaultOmniReport: OmniReport | null;
  reportCategory: ReportCategory;
}

const reportFormValidationSchema = yup.object().shape({
  name: yup.string().required().min(1),
  contentId: yup.string().when('contentType', {
    is: OmniReportContentType.BUILDER,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required().min(1),
  }),
  contentType: yup
    .mixed<OmniReportContentType>()
    .oneOf([
      OmniReportContentType.DASHBOARD,
      OmniReportContentType.APP,
      OmniReportContentType.BUILDER,
    ])
    .required(),
  system: yup.boolean().notRequired(),
});

type ReportFormDTO = yup.InferType<typeof reportFormValidationSchema>;

const OmniReportDialog = ({
  companyId,
  open,
  onClose,
  onSuccess,
  defaultOmniReport,
  reportCategory,
}: ReportCategoryDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { createOmniReport, updateOmniReport } = useOmniReports();

  const isEdit = defaultOmniReport !== null;
  // System updates are only allowed for TREAD company and on Create
  const canAddSystem = !isEdit && companyId === TREAD_UUID;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(reportFormValidationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      name: defaultOmniReport?.name ?? '',
      contentId: defaultOmniReport?.contentId ?? '',
      contentType: defaultOmniReport?.contentType ?? OmniReportContentType.DASHBOARD,
      system: Boolean(defaultOmniReport?.system),
    },
  });

  useEffect(() => {
    reset({
      name: defaultOmniReport?.name ?? '',
      contentId: defaultOmniReport?.contentId ?? '',
      contentType: defaultOmniReport?.contentType ?? OmniReportContentType.DASHBOARD,
      system: Boolean(defaultOmniReport?.system),
    });
  }, [defaultOmniReport, reset]);

  const selectedContentType = watch('contentType');
  const selectedContentTypeIsApp = selectedContentType === OmniReportContentType.APP;
  const selectedContentTypeIsBuilder =
    selectedContentType === OmniReportContentType.BUILDER;

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const onSubmit = useCallback(() => {
    handleSubmit(async (data: ReportFormDTO) => {
      const contentId =
        data.contentType === OmniReportContentType.BUILDER
          ? OMNI_BUILDER_CONTENT_ID
          : (data.contentId ?? '');

      const omniReportData = new OmniReport(
        defaultOmniReport?.id ?? '',
        data.name,
        contentId,
        data.contentType,
        data.system ?? false,
        reportCategory,
      );

      setIsLoading(true);
      try {
        if (!isEdit) {
          const report = await createOmniReport(companyId, omniReportData);
          if (report) {
            onSuccess(report);
          }
        } else {
          const report = await updateOmniReport(companyId, omniReportData);
          if (report) {
            onSuccess(report);
          }
        }
        handleClose();
      } finally {
        setIsLoading(false);
      }
    })();
  }, [companyId, handleClose, onSuccess, isEdit, defaultOmniReport?.id]);

  return (
    <Dialog isOpen={open} onClose={handleClose}>
      <DialogHeader onClose={handleClose}>
        {isEdit
          ? t('administration.report_settings.edit_report')
          : t('administration.report_settings.add_report')}
      </DialogHeader>
      <DialogContent>
        <TextFormField
          control={control}
          errors={errors}
          isRequired
          label={t('form_fields.name')}
          name="name"
          sx={{ mb: 1 }}
        />

        <RadioGroupFormField
          control={control}
          disabled={isEdit}
          errors={errors}
          label={t('administration.report_settings.content_type')}
          list={[
            {
              label: t('administration.report_settings.content_type_dashboard'),
              value: OmniReportContentType.DASHBOARD,
            },
            {
              label: t('administration.report_settings.content_type_app'),
              value: OmniReportContentType.APP,
            },
            {
              label: t('administration.report_settings.content_type_builder'),
              value: OmniReportContentType.BUILDER,
            },
          ]}
          name="contentType"
          sx={{ mb: 1 }}
        />

        {!selectedContentTypeIsBuilder && (
          <TextFormField
            control={control}
            errors={errors}
            isRequired
            label={
              selectedContentTypeIsApp
                ? t('form_fields.app_id')
                : t('form_fields.dashboard_id')
            }
            name="contentId"
            sx={{ mb: 1 }}
          />
        )}

        {canAddSystem && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <CheckBoxFormField
              control={control}
              errors={errors}
              label={t('common.system')}
              name="system"
              value={false}
              sx={{ mr: -2 }}
            />

            <Tooltip
              arrow
              placement="right"
              slotProps={{
                popper: { modifiers: [{ name: 'offset', options: { offset: [0, -8] } }] },
              }}
              title={t('administration.report_settings.system_tooltip')}
            >
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <SmallButton variant="outlined" color="secondary" onClick={handleClose}>
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          color="primary"
          loading={isLoading}
          onClick={onSubmit}
          variant="contained"
        >
          {isEdit ? t('actions.save') : t('actions.create')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default OmniReportDialog;
